import React, { useRef, useState } from "react";
import UploadIcon from "@mui/icons-material/Upload";
import { IconButton, CircularProgress, Box } from "@mui/material";
import { AxiosResponse } from "axios";
import axiosInstance from "utilities/axios";
import useDialog from "hooks/useDialog";

interface FileUploadCGSProps {
    uploadUrl: string;
    fieldName?: string;
    accept?: string;
    confirmMessage?: string;
    onUploadSuccess?: (data: any) => void;
    onUploadError?: (error: any) => void;
    disabled?: boolean;
}

const isSuspiciousFile = (file: File): boolean => {
    const name = file.name.toLowerCase();

    if (name.split(".").length > 2) return true;

    return FORBIDDEN_EXTENSIONS.some(ext => name.endsWith(ext));
};


export const FORBIDDEN_EXTENSIONS = [
    // executables
    ".exe", ".msi", ".bat", ".cmd", ".com", ".scr",

    // scripts
    ".js", ".jse", ".ts", ".py", ".rb", ".pl",
    ".sh", ".bash", ".zsh", ".ps1",

    // server-side scripts
    ".php", ".phtml", ".asp", ".aspx", ".jsp", ".cgi",

    // java / bytecode
    ".jar", ".class", ".war", ".ear",

    // macro-enabled office
    ".docm", ".xlsm", ".pptm", ".dotm", ".xltm",

    // archives
    ".zip", ".rar", ".7z", ".tar", ".gz", ".bz2", ".xz",

    // disk images
    ".iso", ".img", ".dmg",

    // shortcuts
    ".lnk", ".url",
] as const;

const FileUploadCGS: React.FC<FileUploadCGSProps> = ({
    uploadUrl,
    fieldName = "file",
    accept,
    confirmMessage = "გსურთ ფაილის ატვირთვა?",
    onUploadSuccess,
    onUploadError,
    disabled = false
}) => {
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { promptConfirm, showSnackbar } = useDialog();

    const handleUploadClick = () => fileInputRef.current?.click();
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (isSuspiciousFile(file)) {
            showSnackbar?.(
                "უსაფრთხოების მიზნით აღნიშნული ფაილის ატვირთვა აკრძალულია",
                "error"
            );

            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const confirmed = await promptConfirm({
            title: "დადასტურება",
            message: confirmMessage,
        });

        if (!confirmed) {
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append(fieldName, file);

        try {
            const response: AxiosResponse<any> = await axiosInstance.post(uploadUrl, formData);
            onUploadSuccess?.(response.data);
        } catch (err: any) {
            if (onUploadError) onUploadError(err);
            else showSnackbar?.("ფაილის ატვირთვა ვერ მოხერხდა111", "error");
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <Box display="flex" alignItems="center" gap={0.5}>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept={accept}
                multiple={false}
            />
            {loading
                ? <CircularProgress size={16} />
                : <IconButton disabled={disabled} onClick={handleUploadClick} size="small" sx={{
                    padding: 0,
                    width: 20,
                    height: 20,
                }}>
                    <UploadIcon fontSize="small" sx={{ fontSize: 20, color: !loading && !disabled ? "#1976d2" : 'rgb(0,0,0,0.23)' }} />
                </IconButton>}
        </Box>
    );
};

export default FileUploadCGS;
